/* global React, Icon, Button, Card, DataList, EmptyState, Modal, fmt, t, PlummeI18n */
// ============================================================
// PLUMME — AI Rådgiver: Salg og lønnsomhet
// ============================================================
// Siden orkestrerer bare. Regnestykket ligger i services/aiAdvisorSales.jsx,
// og state-filtreringen i backend.getAiAdvisorSalesModel.
const { useState: asUseState } = React;

const aiiSalgTrendIcon = { up: 'trendUp', down: 'trendDown', flat: 'sliders', new: 'sparkle' };
const aiiSalgTrendColor = { up: 'var(--success)', down: 'var(--error)', flat: 'var(--muted)', new: 'var(--plum-600)' };
const aiiPct = (value) => value == null
  ? '—'
  : `${value > 0 ? '+' : ''}${Number(value).toLocaleString(PlummeI18n.getLocale(), { maximumFractionDigits: 1 })} %`;
const aiiDays = (value) => value == null ? null : t('adv.sales.days', { days: value });

function AiSalgKpi({ label, value, period, tone = 'plum', help }) {
  return <div className={'aii-salg-kpi tone-' + tone} {...(help ? { 'data-help': help, 'data-help-title': label } : {})}>
    <span className="lbl">{label}</span>
    <span className="val">{value}</span>
    {period && <span className="period">{period}</span>}
  </div>;
}

function AiSalgCustomerTable({ model }) {
  return <div className="aii-section-frame">
    <window.AiSecHead icon="users" title={t('adv.sales.customers')} sub={t('adv.sales.customersSub')} help={t('adv.sales.customersHelp')} />
    <Card><DataList tableClass="aii-customer-list" rows={model.customers} rowKey={(c) => c.key}
      paginationAside={model.customers.some((c) => c.grossProfit == null) ? <span className="t-xs muted">{t('adv.sales.profitNote')}</span> : null}
      columns={[
      { header: t('adv.sales.col.customer'), render: (c) => <b>{c.name}</b>, sort: (c) => c.name },
      { header: t('adv.sales.col.revenue'), headerRight: true, cellClass: 'num', render: (c) => fmt(c.revenue), sort: (c) => c.revenue },
      { header: t('adv.sales.col.grossProfit'), headerRight: true, cellClass: 'num', render: (c) => c.grossProfit == null ? t('adv.sales.partialProfit') : fmt(c.grossProfit), sort: (c) => c.grossProfit ?? -Infinity },
      { header: t('adv.sales.col.paymentSpeed'), headerRight: true, cellClass: 'num', render: (c) => c.avgPaymentDays == null ? t('adv.sales.noPayments') : aiiDays(c.avgPaymentDays), sort: (c) => c.avgPaymentDays ?? -Infinity },
      { header: t('adv.sales.col.discount'), headerRight: true, cellClass: 'num', render: (c) => aiiPct(c.discountPct).replace('+', ''), sort: (c) => c.discountPct },
      { header: t('adv.sales.col.jobs'), headerRight: true, cellClass: 'num', render: (c) => c.jobs, sort: (c) => c.jobs },
      { header: t('adv.sales.col.trend'), render: (c) => <span title={c.revenueChangePct == null ? t('adv.sales.trendNew') : aiiPct(c.revenueChangePct)}><Icon name={aiiSalgTrendIcon[c.trend]} size={15} style={{ color: aiiSalgTrendColor[c.trend] }} /></span>, sort: (c) => c.revenueChangePct ?? -Infinity },
    ]} /></Card>
  </div>;
}

const AII_PRODUCT_PAGE_SIZE = 3;

function AiSalgProductCard({ kind, products, page = 0, onPageChange }) {
  const label = t('adv.sales.' + kind);
  const help = t('adv.help.' + (kind === 'topRevenue' ? 'topRevenue' : kind === 'growing' ? 'growingSales' : 'fallingSales'));
  const shown = products.slice(page * AII_PRODUCT_PAGE_SIZE, (page + 1) * AII_PRODUCT_PAGE_SIZE);
  const pages = Math.ceil(products.length / AII_PRODUCT_PAGE_SIZE);
  return <div className={'aii-prod-col ' + (kind === 'falling' ? 'warn' : 'good')}>
    <div className="aii-prod-lbl" data-help-title={label} data-help={help}>{label}</div>
    {shown.length ? shown.map((p) => <div key={p.name} className="aii-prod-row">
      <span>{p.name}</span><b>{kind === 'topRevenue' ? fmt(p.revenue) : aiiPct(p.revenueChangePct)}</b>
    </div>) : <div className="aii-prod-row muted"><span>{t('adv.sales.noComparison')}</span></div>}
    {onPageChange && pages > 1 && <div className="aii-prod-pagination">
      <button type="button" onClick={() => onPageChange(page - 1)} disabled={page === 0} aria-label={t('adv.sales.productsPrevious', { label })}><Icon name="chevLeft" size={16} /></button>
      <span>{t('adv.sales.productsPage', { page: page + 1, pages })}</span>
      <button type="button" onClick={() => onPageChange(page + 1)} disabled={page + 1 === pages} aria-label={t('adv.sales.productsNext', { label })}><Icon name="chevRight" size={16} /></button>
    </div>}
  </div>;
}

function AiSalgProductSection({ model }) {
  const [showAll, setShowAll] = asUseState(false);
  const [pages, setPages] = asUseState({ topRevenue: 0, growing: 0, falling: 0 });
  const best = model.products;
  const growing = model.products.filter((p) => p.revenueChangePct != null && p.revenueChangePct > 0)
    .sort((a, b) => b.revenueChangePct - a.revenueChangePct);
  const falling = model.products.filter((p) => p.revenueChangePct != null && p.revenueChangePct < 0)
    .sort((a, b) => a.revenueChangePct - b.revenueChangePct);
  const groups = { topRevenue: best, growing, falling };
  const hasMore = Object.values(groups).some((products) => products.length > AII_PRODUCT_PAGE_SIZE);
  if (!model.products.length) return null;
  return <div className="aii-section-frame">
    <window.AiSecHead icon="box" title={t('adv.sales.products')} sub={t('adv.sales.productsSub')} help={t('adv.sales.productsHelp')}
      right={hasMore ? <Button variant="ghost" size="sm" iconRight="chevRight" onClick={() => setShowAll(true)}>{t('adv.sales.productsShowMore')}</Button> : null} />
    <Card><div className="aii-prod-grid">
      {Object.entries(groups).map(([kind, products]) => <AiSalgProductCard key={kind} kind={kind} products={products} />)}
    </div><p className="t-xs muted" style={{ margin: '14px 0 0' }}>{t('adv.sales.productsNote')}</p></Card>
    {showAll && <Modal open onClose={() => setShowAll(false)} title={t('adv.sales.products')} max={1440} maxHeight="86vh" center portal className="aii-prio-more-modal aii-products-modal"
      footer={<Button onClick={() => setShowAll(false)}>{t('common.close')}</Button>}>
      <div className="aii-prod-grid">
        {Object.entries(groups).map(([kind, products]) => <AiSalgProductCard key={kind} kind={kind} products={products}
          page={Math.min(pages[kind], Math.max(0, Math.ceil(products.length / AII_PRODUCT_PAGE_SIZE) - 1))}
          onPageChange={(page) => setPages((current) => ({ ...current, [kind]: page }))} />)}
      </div>
    </Modal>}
  </div>;
}

// Tallformat per effekttype. Dager er ikke kroner, og prosent er ikke et beløp —
// uten dette ville «9 dager» blitt vist som «9 kr».
const aiiSalgEffect = (move) => {
  if (move.effectKind === 'amount') return fmt(move.effectValue);
  if (move.effectKind === 'days') return t('adv.sales.days', { days: move.effectValue });
  return aiiPct(move.effectValue).replace('+', '');
};
const aiiSalgVars = (vars) => {
  const out = {};
  Object.entries(vars || {}).forEach(([key, value]) => {
    out[key] = /^(amount|revenue|delta)$/.test(key) && Number.isFinite(Number(value)) ? fmt(Number(value)) : value;
  });
  return out;
};

// ---------- Dette ville jeg gjort først ----------
function AiSalgMoves({ moves, onNav }) {
  if (!moves?.length) return null;
  return <div className="aii-section-frame">
    <window.AiSecHead icon="sparkle" title={t('adv.sales.moves.title')} sub={t('adv.sales.moves.sub')} help={t('adv.sales.moves.help')} />
    <div className="aii-prio-grid" style={{ marginTop: 14 }}>
      {moves.map((move, i) => <div key={move.key} className="aii-prio imp">
        <div className="aii-prio-head">
          <span className="aii-prio-ico"><Icon name={move.icon} size={19} /></span>
          <span className="aii-prio-rank">{i + 1}</span>
        </div>
        <div className="aii-prio-title">{t('adv.sales.move.' + move.key + '.title')}</div>
        <div className="aii-prio-text">{t('adv.sales.move.' + move.key + '.text', aiiSalgVars(move.vars))}</div>
        <div className="aii-prio-gain">
          <span className="lbl">{t('adv.sales.move.' + move.key + '.effectLbl')}</span>
          <span className="val">{aiiSalgEffect(move)}</span>
          <span className="note">{t('adv.sales.move.' + move.key + '.effectNote')}</span>
        </div>
        <div className="aii-prio-tags">
          {window.AiiEffectTag && <window.AiiEffectTag type={move.effectType} />}
          {window.AiiConfidenceTag && <window.AiiConfidenceTag level={move.confidence} />}
        </div>
        {move.route && <div className="aii-prio-actions aii-salg-prio-actions">
          <button type="button" className="aii-opp-link" onClick={() => onNav && onNav(move.route)}>
            {t('adv.sales.move.' + move.key + '.action')}<Icon name="chevRight" size={12} />
          </button>
        </div>}
      </div>)}
    </div>
  </div>;
}

// ---------- Salgsscenarioer ----------
// Før/etter på fire nøkkeltall. Marginen er prosentpoeng, ikke kroner, og formatteres
// derfor for seg.
function AiSalgScenarioTable({ scenario }) {
  const rows = [
    { key: 'revenue', kind: 'amount' },
    { key: 'result', kind: 'amount' },
    { key: 'margin', kind: 'points' },
    { key: 'liquidity', kind: 'amount' },
  ].filter((row) => scenario.before[row.key] != null && scenario.after[row.key] != null);
  return <div className="aii-scn-table">
    <div className="aii-scn-table-row aii-scn-table-head">
      <span>{t('adv.sales.scn.metric')}</span><span>{t('adv.sales.scn.base')}</span>
      <span>{t('adv.sales.scn.after')}</span><span>{t('adv.sales.scn.change')}</span>
    </div>
    {rows.map((row) => {
      const before = scenario.before[row.key];
      const after = scenario.after[row.key];
      const diff = after - before;
      const show = (value) => row.kind === 'amount' ? fmt(value) : aiiPct(value).replace('+', '');
      return <div key={row.key} className="aii-scn-table-row">
        <span>{t('adv.sales.scn.row.' + row.key)}</span>
        <span>{show(before)}</span>
        <span>{show(after)}</span>
        <span style={{ color: diff > 0 ? 'var(--success)' : diff < 0 ? 'var(--error)' : 'var(--muted)' }}>
          {row.kind === 'amount'
            ? `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt(Math.abs(diff))}`
            : t('adv.sales.scn.points', { points: aiiPct(diff).replace(' %', '') })}
        </span>
      </div>;
    })}
  </div>;
}

function AiSalgScenarioComparison({ scenario }) {
  const metrics = ['revenue', 'result', 'liquidity'].filter((key) =>
    Number.isFinite(scenario.before[key]) && Number.isFinite(scenario.after[key]));
  if (!metrics.length) return null;
  return <div className="aii-scn-comparison-cards">{metrics.map((key) => {
      const before = scenario.before[key];
      const after = scenario.after[key];
      const diff = after - before;
      return <div key={key} className="aii-scn-comparison-card">
        <h3>{t('adv.sales.scn.row.' + key)}</h3>
        <div className="aii-scn-comparison-values">
          <div><span>{t('adv.sales.scn.base')}</span><b>{fmt(before)}</b></div>
          <Icon name="arrowRight" size={18} />
          <div className="after"><span>{t('adv.sales.scn.after')}</span><b>{fmt(after)}</b></div>
        </div>
        <div className={'aii-scn-comparison-change ' + (diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral')}>
          {t('adv.sales.scn.change')}: {diff > 0 ? '+' : diff < 0 ? '−' : ''}{fmt(Math.abs(diff))}
        </div>
      </div>;
    })}
  </div>;
}

function AiSalgScenarios({ scenarios, onPercentChange }) {
  const [active, setActive] = asUseState(scenarios[0]?.id || null);
  const [showAssumptions, setShowAssumptions] = asUseState(false);
  if (!scenarios.length) return null;
  const scenario = scenarios.find((entry) => entry.id === active) || scenarios[0];
  return <div className="aii-section-frame">
    <window.AiSecHead icon="sparkle" title={t('adv.sales.scn.title')} sub={t('adv.sales.scn.sub')} help={t('adv.sales.scn.help')} />
    <Card>
    <div className="aii-scn-chips" style={{ marginTop: 10 }}>
      {scenarios.map((entry) => <button key={entry.id} type="button"
        className={'aii-scn-chip' + (scenario.id === entry.id ? ' on' : '')} onClick={() => { setActive(entry.id); setShowAssumptions(false); }}>
        <Icon name={entry.icon} size={15} />{t('adv.sales.scn.q.' + entry.id, aiiSalgVars(entry.qVars))}
      </button>)}
    </div>
    {scenario.slider && <div className="aii-sales-scenario-slider">
      <label htmlFor="aii-sales-scenario-percent">{t('adv.sales.scn.adjust.' + scenario.id)} <b>{scenario.slider.value} %</b></label>
      <input id="aii-sales-scenario-percent" type="range" min={scenario.slider.min} max={scenario.slider.max} step={scenario.slider.step}
        value={scenario.slider.value} onChange={(event) => onPercentChange(scenario.id, Number(event.target.value))} />
      <div className="aii-sales-scenario-slider-limits"><span>0 %</span><span>100 %</span></div>
    </div>}
    <AiSalgScenarioComparison scenario={scenario} />
    <AiSalgScenarioTable scenario={scenario} />
    <div className="aii-scn-assumptions-action"><button type="button" onClick={() => setShowAssumptions(true)}>
      {t('adv.sales.scn.showAssumptions')}<Icon name="chevRight" size={14} />
    </button></div>
    <div className={'aii-scn-result tone-' + scenario.tone}>
      <p>{t('adv.sales.scn.summary.' + scenario.id, aiiSalgVars(scenario.summaryVars))}</p>
    </div>
    </Card>
    <Modal open={showAssumptions} onClose={() => setShowAssumptions(false)}
      title={t('adv.sales.scn.assumptions')} max={520} portal>
      <p className="aii-scn-assumption-intro">{t('adv.sales.scn.assumption.' + scenario.notes.assumptions)}</p>
      <dl className="aii-scn-assumptions-list">
        <div><dt>{t('adv.sales.scn.confidence')}</dt><dd>{t('adv.confLevel.' + scenario.notes.confidence)}</dd></div>
        <div><dt>{t('adv.sales.scn.risk')}</dt><dd>{t('adv.sales.scn.riskText.' + scenario.notes.risk)}</dd></div>
        <div><dt>{t('adv.sales.scn.scope')}</dt><dd>{t('adv.sales.scn.scopeText.' + scenario.notes.scope)}</dd></div>
      </dl>
    </Modal>
  </div>;
}

function AiSalgTab({ year, onNav, financials, liquidity }) {
  const [scenarioInputs, setScenarioInputs] = asUseState({});
  // Salgsmodellen slår opp betalinger per faktura og er derfor tyngst av alle fanene
  // på et stort datasett. Den skal bygges når tallene endrer seg, ikke ved hver render.
  const model = React.useMemo(
    () => window.PlummeBackend?.getAiAdvisorSalesModel?.({ year }) || null,
    [year, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState],
  );
  if (!model || !model.invoiceCount) {
    return <Card><EmptyState noVisual acc="sparkle" title={t('adv.sales.emptyTitle')} text={t('adv.sales.emptyText')} compact /></Card>;
  }
  const k = model.kpis;
  // Resultat og likviditet eies av rådgivermodellen, ikke av salgsmodellen. Siden
  // sender dem inn — salgsmodellen skal ikke regne ut et resultat på egen hånd.
  const advice = globalThis.PlummeAiAdvisorSalesAdvice?.buildSalesAdvice?.(model, {
    result: financials?.result ?? 0,
    liquidity: liquidity ?? 0,
  }, scenarioInputs) || null;
  return <React.Fragment>
    {model.importedHistory && <div className="bg-note info" style={{ marginBottom: 16 }}><Icon name="archive" size={18} /><span>{t('adv.sales.importedHistoryNote', {
      coverage: t(model.importedHistory.coverage === 'invoice-lines' ? 'adv.sales.importedLinesCoverage' : 'adv.sales.importedTotalsCoverage'),
    })}</span></div>}
    {/* Egen markup i stedet for <AiVerdict>: den komponenten oversetter via
        adv.verdict.*, mens salgsdommen har sine egne nøkler. */}
    {advice?.verdict && <div className={'aii-verdict tone-' + advice.verdict.tone}>
      <div className="aii-verdict-main">
        <span className="aii-verdict-avatar"><img src={aiiPlumSrc()} alt="SuperPlumma" /></span>
        <div className="aii-verdict-body">
          <div className="aii-verdict-tag"><Icon name="sparkle" size={14} />{t('adv.sales.verdictTag')}</div>
          <p className="aii-verdict-text">{t('adv.sales.verdict.' + advice.verdict.key, aiiSalgVars(advice.verdict.vars))}</p>
        </div>
      </div>
    </div>}
    <Card>
      <window.AiSecHead icon="trendUp" title={t('adv.sales.title')} sub={t('adv.sales.sub', { year: model.year })} help={t('adv.sales.help')} />
      <div className="aii-salg-kpis" style={{ marginTop: 12 }}>
        <AiSalgKpi label={t('adv.sales.kpi.revenue')} value={fmt(model.revenue)} period={t('adv.sales.periodYear', { year: model.year })} tone="green" help={t('adv.help.revenue')} />
        <AiSalgKpi label={t('adv.sales.kpi.growth')} value={aiiPct(k.revenueChangePct)}
          period={t('adv.sales.periodVsPrev', { year: model.year - 1 })}
          tone={k.revenueChangePct == null ? 'plum' : k.revenueChangePct >= 0 ? 'green' : 'amber'}
          help={t('adv.sales.kpi.growthHelp')} />
        <AiSalgKpi label={t('adv.sales.kpi.avgInvoice')} value={fmt(k.avgInvoice)} period={t('adv.sales.invoiceCount', { count: model.invoiceCount })} tone="plum" help={t('adv.help.avgInvoice')} />
        <AiSalgKpi label={t('adv.sales.kpi.discount')} value={aiiPct(k.avgDiscountPct).replace('+', '')}
          period={t('adv.sales.periodYear', { year: model.year })} tone={k.avgDiscountPct > 10 ? 'amber' : 'plum'}
          help={t('adv.sales.kpi.discountHelp')} />
        <AiSalgKpi label={t('adv.sales.kpi.paymentDays')}
          value={k.avgPaymentDays == null ? t('adv.sales.noPayments') : aiiDays(k.avgPaymentDays)}
          period={t('adv.sales.periodYear', { year: model.year })}
          tone={k.avgPaymentDays != null && k.avgPaymentDays > 30 ? 'amber' : 'green'}
          help={t('adv.sales.kpi.paymentDaysHelp')} />
        <AiSalgKpi label={t('adv.sales.kpi.repeat')} value={aiiPct(k.repeatRatePct).replace('+', '')}
          period={t('adv.sales.customerCount', { count: model.customerCount })} tone="plum"
          help={t('adv.sales.kpi.repeatHelp')} />
        <AiSalgKpi label={t('adv.sales.kpi.concentration')}
          value={k.topCustomerSharePct == null ? '—' : aiiPct(k.topCustomerSharePct).replace('+', '')}
          period={k.topCustomerName || t('adv.sales.periodYear', { year: model.year })}
          tone={k.topCustomerSharePct != null && k.topCustomerSharePct > 40 ? 'amber' : 'green'}
          help={t('adv.sales.kpi.concentrationHelp')} />
        <AiSalgKpi label={t('adv.sales.kpi.recurring')} value={aiiPct(k.recurringSharePct).replace('+', '')}
          period={t('adv.sales.recurringCount', { count: k.recurringCount })} tone="plum"
          help={t('adv.sales.kpi.recurringHelp')} />
      </div>
    </Card>
    {advice?.moves?.length > 0 && <AiSalgMoves moves={advice.moves} onNav={onNav} />}
    <AiSalgCustomerTable model={model} />
    <AiSalgProductSection model={model} />
    {advice?.scenarios?.length > 0 && <AiSalgScenarios scenarios={advice.scenarios}
      onPercentChange={(id, value) => setScenarioInputs((current) => ({ ...current, [id]: value }))} />}
  </React.Fragment>;
}

Object.assign(window, { AiSalgTab });
